import { useState, type ReactNode } from 'react';
import type { MeasureResult } from './api';
import type { ThemeChoice } from './theme';

/**
 * Компоненты из дизайн-передачи. Значения не подбираются на глаз —
 * все размеры и цвета идут из токенов @lma/tokens.
 */

export function Overline({ children }: { children: ReactNode }) {
  return <p className="overline">{children}</p>;
}

export function Secondary({ children }: { children: ReactNode }) {
  return <p className="secondary">{children}</p>;
}

export function LegalNote({ children }: { children: ReactNode }) {
  return <p className="legal">{children}</p>;
}

export function Highlight({ children }: { children: ReactNode }) {
  return <p className="highlight">{children}</p>;
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button className="btn-primary" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  /** Нужен там, где нажатие уходит на сервер: сохранение наблюдения. */
  disabled?: boolean;
}) {
  return (
    <button className="btn-secondary" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function TertiaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  /** Нужен там, где нажатие уходит на сервер: отключение подписки, например. */
  disabled?: boolean;
}) {
  return (
    <button className="btn-tertiary" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/** Кнопка выбора: минимальная высота 60, текст в две строки помещается. */
export function ChoiceButton({
  children,
  onClick,
  selected,
}: {
  children: ReactNode;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button className={`btn-choice${selected ? ' selected' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

/** Блок «чего из этого нельзя заключить» и предупреждения в пробах. */
export function CautionBlock({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="block-caution">
      {title ? <span className="block-label">{title}</span> : null}
      <div>{children}</div>
    </div>
  );
}

/** Красные флаги и медицинский маршрут. */
export function StopBlock({ title, items }: { title?: string; items: string[] }) {
  return (
    <div className="block-stop">
      {title ? <span className="block-label">{title}</span> : null}
      <ul className="dash-list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function DashList({ items }: { items: string[] }) {
  return (
    <ul className="dash-list plain">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

/** Нумерованные шаги пробы. Номер в кружке 28×28. */
export function StepsList({ steps }: { steps: string[] }) {
  return (
    <ol className="steps-list">
      {steps.map((step, index) => (
        <li key={step}>
          <span className="step-number">{index + 1}</span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  );
}

export { VideoBlock } from './video';
import { IconMoon, IconSun, NAV_ICONS, type NavKey } from './icons';

export function ProgressDots({
  current,
  total,
  label,
}: {
  current: number;
  total: number;
  label: string;
}) {
  return (
    <div className="progress">
      <div className="dots">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`dot${i + 1 === current ? ' current' : i + 1 < current ? ' passed' : ''}`}
          />
        ))}
      </div>
      <span className="progress-label">{label}</span>
    </div>
  );
}

/**
 * Шкала замера 0–10 — самый частый компонент продукта.
 *
 * Значение обязательно сохраняется на сервере: оценка, которая нигде
 * не записана, не даёт ни графика, ни доказательства, ни итога семи дней.
 */
export function Scale({
  value,
  onChange,
  lowLabel,
  highLabel,
}: {
  value: number | null;
  onChange: (value: number) => void;
  lowLabel: string;
  highLabel: string;
}) {
  return (
    <div className="scale-wrap">
      <div className="scale" role="radiogroup">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={value === i}
            className={`scale-cell${value === i ? ' selected' : ''}`}
            onClick={() => onChange(i)}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="scale-labels">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

/**
 * Мгновенный отклик после пробы: её собственные цифры.
 *
 * Ухудшение показывается нейтральным цветом, не красным. Пугать здесь нельзя —
 * это прямое требование дизайна, а не вкусовое решение.
 */
export function MeasureResultBlock({ result, note }: { result: MeasureResult; note: string }) {
  return (
    <div className="stack">
      <div className="measure-result">
        <div className="measure-side">
          <span className="measure-label">До</span>
          <span className="measure-digit">{result.before}</span>
        </div>
        <span className="measure-arrow" aria-hidden="true" />
        <div className="measure-side">
          <span className="measure-label">После</span>
          <span className="measure-digit">{result.after}</span>
        </div>
        <span className={`measure-delta ${result.verdict}`}>{result.label}</span>
      </div>
      <LegalNote>{note}</LegalNote>
    </div>
  );
}

/** Загрузка — скелетон, не спиннер. */
export function ScreenSkeleton() {
  return (
    <main className="screen">
      <div className="stack">
        <div className="skeleton" style={{ width: '35%' }} />
        <div className="skeleton" style={{ width: '80%', height: 34 }} />
        <div className="skeleton tall" />
        <div className="skeleton" style={{ width: '60%' }} />
      </div>
    </main>
  );
}

/**
 * Нижнее меню.
 *
 * Шесть пунктов в панель на мобильном не помещаются: четыре главных внизу,
 * остальные — листом снизу. У каждого пиктограмма и подпись 11 пикселей,
 * как в макете: одни подписи читаются хуже и выглядят как черновик.
 */
export function Nav({
  active,
  onSelect,
  extraItems,
  resolved,
  onThemeChange,
}: {
  active: string | null;
  onSelect: (key: string) => void;
  extraItems: { key: NavKey; label: string }[];
  /** Какая тема сейчас на экране — от неё зависит, что предлагает кнопка. */
  resolved: 'light' | 'dark';
  onThemeChange: (choice: ThemeChoice) => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  // Тип ключа не даёт завести пункт без иконки: раньше такой пункт
  // просто рисовался без значка и выбивался из ряда.
  const main: { key: NavKey; label: string }[] = [
    { key: 'obs7', label: '7 дней' },
    { key: 'passport14', label: '14 дней' },
    { key: 'program12', label: '12 недель' },
  ];

  return (
    <>
      {sheetOpen ? (
        <div className="sheet-backdrop" onClick={() => setSheetOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            {extraItems.map((item) => {
              const Icon = NAV_ICONS[item.key];
              return (
                <button
                  key={item.key}
                  className="sheet-item"
                  onClick={() => {
                    setSheetOpen(false);
                    onSelect(item.key);
                  }}
                >
                  <Icon className="sheet-icon" />
                  <span>{item.label}</span>
                </button>
              );
            })}

            <button
              className="sheet-item"
              onClick={() => onThemeChange(resolved === 'dark' ? 'light' : 'dark')}
              aria-label={resolved === 'dark' ? 'Включить светлое' : 'Включить тёмное'}
            >
              {resolved === 'dark' ? (
                <IconSun className="sheet-icon" />
              ) : (
                <IconMoon className="sheet-icon" />
              )}
              <span>{resolved === 'dark' ? 'Светлое оформление' : 'Тёмное оформление'}</span>
            </button>
          </div>
        </div>
      ) : null}

      <nav className="nav">
        {main.map((item) => {
          const Icon = NAV_ICONS[item.key];
          return (
            <button
              key={item.key}
              className={`nav-item${active === item.key ? ' active' : ''}`}
              onClick={() => onSelect(item.key)}
            >
              <Icon className="nav-icon" />
              <span>{item.label}</span>
            </button>
          );
        })}
        <button
          className={`nav-item${sheetOpen ? ' active' : ''}`}
          onClick={() => setSheetOpen(true)}
        >
          {(() => {
            const Icon = NAV_ICONS.more!;
            return <Icon className="nav-icon" />;
          })()}
          <span>Ещё</span>
        </button>
      </nav>
    </>
  );
}
